import Delete from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material'
import {
  DataGridPro,
  GridActionsCellItem,
  GridRenderCellParams,
  GridRowsProp,
  GridValueFormatterParams
} from '@mui/x-data-grid-pro'
import { FC, useCallback, useMemo, useState } from 'react'

import { useUpdateCannedJobDate } from '../../hooks'
import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { useGetPartsMatrices } from '../../hooks/useGetPartsMatrices/useGetPartsMatrices'
import { useGetVendorEntries } from '../../hooks/useGetVendorEntries/useGetVendorEntries'
import { getInventoryPartName } from '../../hooks/useTransformServiceItems/useTransformServiceItems'
import { LabelsPartsInfo } from '../../models/Labels'
import {
  useDeleteCannedJobCustomPartMutation,
  useDeleteCannedJobMotorFluidsMutation,
  useDeleteCannedJobPartLookupMutation,
  useDeleteCannedJobToInventoryPartMutation,
  useDeleteCannedJobToInventoryProductMutation
} from '../../redux/api/cannedJobApi'
import { useGetTaxesAndFeesQuery } from '../../redux/api/shopMatrixApi'
import {
  CannedJobInventoryPartEntry,
  CannedJobMotorFluidsEntry,
  CannedJobPartLookupEntry,
  CannedJobToInventoryProductEntry,
  InventoryPartEntry
} from '../../sdk'
import { CannedJobCustomPartEntry } from '../../sdk/models/CannedJobCustomPartEntry'
import { formatPercentage } from '../../utils'
import { NONE_ID } from '../../utils/convertEmptyStringToUndefined/convertEmptyStringToUndefined'
import { formatCurrencyInDataGrid } from '../../utils/formatCurrencyInDataGrid'
import { CustomFooterUI } from '../SharedUI/CustomFooter'
import {
  getMarginOfCannedPart,
  getPartsMatrixOfCannedPart,
  getPostTaxPrice,
  getRetailPrice,
  getRetailPricePerUnitOfCannedPart
} from './customPartsCalculation'
import { CannedJobPartsDialogUI } from './PartsDialogUI/CannedJobPartsDialogUI'
import { useAddCannedJobInventoryDialog } from './useAddCannedJobInventoryDialog/useAddCannedJobInventoryDialog'
import { useAddCustomPartsDialog } from './useAddCustomPartsDialog/useAddCustomPartsDialog'
import { useAddMotorFluidsDialog } from './useAddMotorFluidsDialog/useAddMotorFluidsDialog'
import { useAddPartLookupDialog } from './useAddPartLookupDialog/useAddPartLookupDialog'

export type PartTypes =
  | 'Custom'
  | 'Lookup'
  | 'InventoryPart'
  | 'InventoryProduct'
  | 'MotorFluids'

export function formatMarginInDataGrid(
  params: GridValueFormatterParams<number>
) {
  if (params.value == null) {
    return ''
  }
  return formatPercentage(params.value / 100)
}
interface Props {
  customParts: CannedJobCustomPartEntry[]
  partLookups: CannedJobPartLookupEntry[]
  inventoryParts: CannedJobInventoryPartEntry[]
  idToInventoryParts: { [key: string]: InventoryPartEntry }
  inventoryProducts: CannedJobToInventoryProductEntry[]
  idToInventoryProducts: { [key: string]: CannedJobCustomPartEntry }
  motorFluids: CannedJobMotorFluidsEntry[]
  disabled?: boolean
  id: string
}

export const CannedJobParts: FC<Props> = ({
  customParts,
  partLookups,
  inventoryParts,
  idToInventoryParts,
  inventoryProducts,
  idToInventoryProducts,
  motorFluids,
  disabled,
  id
}) => {
  const [deleteProps, setDeleteProps] = useState<
    { id: string; type: PartTypes } | undefined
  >()
  const [deleteCustomPart] = useDeleteCannedJobCustomPartMutation()
  const [deletePartLookup] = useDeleteCannedJobPartLookupMutation()
  const [deleteCannedJobInventoryPart] =
    useDeleteCannedJobToInventoryPartMutation()
  const [deleteCannedJobToInventoryProduct] =
    useDeleteCannedJobToInventoryProductMutation()
  const [deleteMotorFluids] = useDeleteCannedJobMotorFluidsMutation()
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { allPartsMatrices } = useGetPartsMatrices([])
  const { updateDate } = useUpdateCannedJobDate()
  const { setCustomProps: setAddCustomPartsProps, customPartsDialogProps } =
    useAddCustomPartsDialog({
      submitCallback: updateDate,
      cannedJobId: id,
      counterSaleId: undefined
    })
  const { setCustomProps: setAddPartLookupProps, AddPartLookupDialog } =
    useAddPartLookupDialog()
  const {
    setCustomProps: setAddInventoryProps,
    AddCannedJobInventoryPartDialog
  } = useAddCannedJobInventoryDialog()
  const { setCustomProps: setAddMotorFluidsProps, AddMotorFluidsDialog } =
    useAddMotorFluidsDialog()

  const { data: taxesAndFees } = useGetTaxesAndFeesQuery({
    authorization: accessToken,
    shop: shopId
  })

  const { shopVendorsAllMap } = useGetVendorEntries()

  const columns = useMemo(
    () => [
      {
        field: 'partNumber',
        headerName: LabelsPartsInfo.partNumber,
        flex: 1
      },
      { field: 'name', headerName: 'Parts ' + LabelsPartsInfo.name, flex: 1 },
      { field: 'type', headerName: 'Type', flex: 1 },
      {
        field: 'quantity',
        headerName: LabelsPartsInfo.quantity,
        flex: 1,
        type: 'number'
      },
      {
        field: 'unitOfMeasure',
        headerName: LabelsPartsInfo.unitOfMeasure,
        flex: 1
      },
      {
        field: 'costPerUnit',
        headerName: LabelsPartsInfo.costPerUnit,
        flex: 1,
        type: 'number',
        valueFormatter: formatCurrencyInDataGrid
      },
      {
        field: 'partsMatrix',
        headerName: LabelsPartsInfo.partsMatrix,
        flex: 1
      },
      {
        field: 'margin',
        headerName: LabelsPartsInfo.margin,
        flex: 1,
        type: 'number',
        valueFormatter: formatMarginInDataGrid
      },
      {
        field: 'retailPricePerUnit',
        headerName: LabelsPartsInfo.retailPricePerUnit,
        flex: 1,
        type: 'number',
        valueFormatter: formatCurrencyInDataGrid
      },
      {
        field: 'retailPrice',
        headerName: LabelsPartsInfo.retailPrice,
        flex: 1,
        valueGetter: getRetailPrice,
        type: 'number',
        valueFormatter: formatCurrencyInDataGrid
      },
      {
        field: 'postTaxPrice',
        headerName: LabelsPartsInfo.postTax,
        flex: 1,
        valueGetter: (params) => getPostTaxPrice(params, taxesAndFees),
        type: 'number',
        valueFormatter: formatCurrencyInDataGrid
      },
      {
        field: 'vendorName',
        headerName: LabelsPartsInfo.vendorName,
        flex: 1
      },
      {
        field: 'description',
        headerName: LabelsPartsInfo.description,
        flex: 1
      },
      {
        field: 'actions',
        type: 'actions',
        flex: 2,
        getActions: (params: GridRenderCellParams) => [
          <GridActionsCellItem
            key="Edit"
            icon={<EditIcon />}
            label="Edit"
            onClick={() => {
              if (params.row.type === 'Custom') {
                setAddCustomPartsProps({
                  existingEntry: customParts.find(
                    (entry) => entry.id === params.id
                  )
                })
              }
              if (params.row.type === 'Lookup') {
                setAddPartLookupProps({
                  cannedJobId: id!,
                  existingEntry: partLookups.find(
                    (entry) => entry.id === params.id
                  )
                })
              }
              if (params.row.type === 'InventoryPart') {
                setAddInventoryProps({
                  cannedJobId: id!,
                  existingEntry: inventoryParts.find(
                    (entry) => entry.id === params.id
                  ),
                  inventoryEntry:
                    idToInventoryParts[params.row.inventoryPartId],
                  existingCannedJobToProductEntry: undefined,
                  productEntry: undefined
                })
              }
              if (params.row.type === 'InventoryProduct') {
                setAddInventoryProps({
                  cannedJobId: id!,
                  existingEntry: undefined,
                  inventoryEntry: undefined,
                  existingCannedJobToProductEntry: inventoryProducts.find(
                    (entry) => entry.id === params.id
                  ),
                  productEntry:
                    idToInventoryProducts[params.row.inventoryProductId]
                })
              }
              if (params.row.type === 'MotorFluids') {
                setAddMotorFluidsProps({
                  cannedJobId: id!,
                  existingEntry: motorFluids.find(
                    (entry) => entry.id === params.id
                  )
                })
              }
            }}
          />,
          <GridActionsCellItem
            icon={<Delete />}
            label="Delete"
            key="Delete"
            onClick={() =>
              setDeleteProps({
                id: params.id! as string,
                type: params.row.type
              })
            }
          />
        ]
      }
    ],
    [
      customParts,
      id,
      idToInventoryParts,
      idToInventoryProducts,
      inventoryParts,
      inventoryProducts,
      motorFluids,
      partLookups,
      setAddCustomPartsProps,
      setAddInventoryProps,
      setAddMotorFluidsProps,
      setAddPartLookupProps,
      taxesAndFees
    ]
  )

  const rows = useMemo(() => {
    const customPartRows: GridRowsProp = customParts.map((data) => {
      const margin = getMarginOfCannedPart(data, allPartsMatrices)
      const retailPricePerUnit = getRetailPricePerUnitOfCannedPart(
        data,
        allPartsMatrices
      )
      const foundMatrix = getPartsMatrixOfCannedPart(data, allPartsMatrices)
      const partsMatrix = foundMatrix
        ? foundMatrix.info!.description!
        : retailPricePerUnit
        ? NONE_ID
        : ''
      const vendorName =
        data.info?.vendorId !== undefined
          ? shopVendorsAllMap.get(data.info?.vendorId)?.info?.name
          : data.info?.supplierName

      return {
        id: data.id!,
        ...data.info!,
        type: 'Custom',
        partsMatrix,
        margin,
        retailPricePerUnit,
        vendorName
      }
    })
    const partLookupRows: GridRowsProp = partLookups.map((data) => {
      const foundMatrix = allPartsMatrices?.find(
        (entry) => entry.id === data.info!.partsMatrix
      )
      return {
        id: data.id!,
        ...data.info!,
        type: 'Lookup',
        name: data.info!.searchTerm!,
        quantity: undefined,
        partsMatrix: foundMatrix ? foundMatrix.info!.description! : '',
        costPerUnit: undefined,
        margin: undefined,
        retailPricePerUnit: undefined
      }
    })
    const inventoryPartRows: GridRowsProp = inventoryParts.map((data) => {
      const foundMatrix = allPartsMatrices?.find(
        (entry) => entry.id === data.info!.partsMatrix
      )
      const foundPart = idToInventoryParts[data.info!.inventoryPart!]
      return {
        id: data.id!,
        ...data.info!,
        type: 'InventoryPart',
        name: getInventoryPartName(foundPart),
        quantity: data.info!.quantity,
        partsMatrix: foundMatrix ? foundMatrix.info!.description! : '',
        costPerUnit: undefined,
        margin: undefined,
        retailPricePerUnit: undefined,
        inventoryPartId: foundPart.id,
        unitOfMeasure: data.info!.unitOfMeasure!,
        partNumber: foundPart.info!.partNumber,
        description: foundPart.info!.description
      }
    })
    const inventoryProductRows: GridRowsProp = inventoryProducts.map((data) => {
      const foundPart = idToInventoryProducts[data.info!.inventoryProduct!]
      const margin = getMarginOfCannedPart(foundPart, allPartsMatrices)
      const retailPricePerUnit = getRetailPricePerUnitOfCannedPart(
        foundPart,
        allPartsMatrices
      )
      const foundMatrix = allPartsMatrices?.find(
        (entry) => entry.id === foundPart?.info!.partsMatrix
      )
      const partsMatrix = foundMatrix
        ? foundMatrix.info!.description!
        : retailPricePerUnit
        ? NONE_ID
        : ''
      const quantity = data.info!.quantity || foundPart.info!.quantity
      return {
        id: data.id!,
        ...foundPart.info!,
        type: 'InventoryProduct',
        quantity,
        partsMatrix,
        margin,
        retailPricePerUnit,
        inventoryProductId: foundPart.id
      }
    })
    const motorFluidsRows: GridRowsProp = motorFluids.map((data) => {
      const foundMatrix = allPartsMatrices?.find(
        (entry) => entry.id === data.info!.partsMatrix
      )
      return {
        id: data.id!,
        ...data.info!,
        type: 'MotorFluids',
        quantity:
          data.info!.suggestedQuantity === undefined
            ? undefined
            : data.info!.suggestedQuantity,
        unitOfMeasure: data.info!.unitOfMeasure,
        partsMatrix:
          data.info!.suggestedPricePerUnit !== undefined
            ? NONE_ID
            : foundMatrix
            ? foundMatrix.info!.description!
            : '',
        costPerUnit: undefined,
        margin: undefined,
        retailPricePerUnit:
          data.info?.suggestedPricePerUnit !== undefined
            ? data.info?.suggestedPricePerUnit
            : undefined
      }
    })
    return customPartRows
      .concat(partLookupRows)
      .concat(inventoryPartRows)
      .concat(inventoryProductRows)
      .concat(motorFluidsRows)
  }, [
    allPartsMatrices,
    customParts,
    idToInventoryParts,
    idToInventoryProducts,
    inventoryParts,
    inventoryProducts,
    motorFluids,
    partLookups,
    shopVendorsAllMap
  ])
  const CustomFooter = () => (
    <CustomFooterUI>
      <Chip
        label="+Custom Part"
        onClick={() => {
          setAddCustomPartsProps({
            existingEntry: undefined
          })
        }}
        disabled={disabled}
      />
      <Chip
        label="+Part Lookup"
        onClick={() => {
          setAddPartLookupProps({
            cannedJobId: id!,
            existingEntry: undefined
          })
        }}
        disabled={disabled}
      />
      <Chip
        label="+Inventory"
        onClick={() => {
          setAddInventoryProps({
            cannedJobId: id!,
            existingEntry: undefined,
            inventoryEntry: undefined,
            existingCannedJobToProductEntry: undefined,
            productEntry: undefined
          })
        }}
        disabled={disabled}
      />
      <Chip
        label="+Fluids"
        onClick={() => {
          setAddMotorFluidsProps({
            cannedJobId: id!,
            existingEntry: undefined
          })
        }}
        disabled={disabled}
      />
    </CustomFooterUI>
  )
  const handleDelete = useCallback(async () => {
    if (!deleteProps) {
      return
    }
    if (deleteProps.type === 'Custom') {
      deleteCustomPart({
        authorization: accessToken,
        cannedJob: id!,
        customPart: deleteProps.id
      })
    }
    if (deleteProps.type === 'Lookup') {
      deletePartLookup({
        authorization: accessToken,
        cannedJob: id!,
        partLookup: deleteProps.id
      })
    }
    if (deleteProps.type === 'InventoryPart') {
      deleteCannedJobInventoryPart({
        authorization: accessToken,
        cannedJob: id!,
        inventoryPart: deleteProps.id
      })
    }
    if (deleteProps.type === 'InventoryProduct') {
      deleteCannedJobToInventoryProduct({
        authorization: accessToken,
        cannedJob: id!,
        inventoryProduct: deleteProps.id
      })
    }
    if (deleteProps.type === 'MotorFluids') {
      deleteMotorFluids({
        authorization: accessToken,
        cannedJob: id!,
        motorFluids: deleteProps.id
      })
    }
    setDeleteProps(undefined)
  }, [
    accessToken,
    deleteCannedJobInventoryPart,
    deleteCannedJobToInventoryProduct,
    deleteCustomPart,
    deleteMotorFluids,
    deletePartLookup,
    deleteProps,
    id
  ])
  return (
    <>
      {customPartsDialogProps && (
        <CannedJobPartsDialogUI {...customPartsDialogProps} />
      )}
      <AddPartLookupDialog />
      <AddCannedJobInventoryPartDialog />
      <AddMotorFluidsDialog />
      <Dialog open={!!deleteProps} onClose={() => setDeleteProps(undefined)}>
        <DialogTitle>Delete Part Item</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this part item?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteProps(undefined)}>Cancel</Button>
          <Button onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <DataGridPro
        rows={rows}
        columns={columns}
        slots={{
          footer: CustomFooter
        }}
        autoHeight
        density="compact"
        sortingOrder={['asc', 'desc']}
      />
    </>
  )
}
